import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dashboardService,
  DashboardResumo,
  PeriodoRelatorio,
  PERIODOS_LABELS,
} from '../../services';

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardResumo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [periodo, setPeriodo] = useState<PeriodoRelatorio>('hoje');
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    loadStats();
  }, [periodo]);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const response = await dashboardService.resumo(periodo);
      setStats(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const exportarCsv = async () => {
    setExportando(true);
    try {
      const response = await dashboardService.exportar(periodo);
      const pedidos = response.data.data;

      const linhas: string[] = [
        ['Data', 'Cliente', 'Mesa', 'Status', 'Total (R$)', 'Itens'].join(';'),
        ...pedidos.map((p) =>
          [
            new Date(p.criadoEm).toLocaleString('pt-BR'),
            `"${p.cliente.replace(/"/g, '""')}"`,
            p.mesa ?? '',
            p.status,
            Number(p.total).toFixed(2).replace('.', ','),
            `"${p.itens.map((i) => `${i.quantidade}x ${i.produto.replace(/"/g, '""')}`).join(' | ')}"`,
          ].join(';'),
        ),
      ];

      const blob = new Blob(['\uFEFF' + linhas.join('\n')], {
        type: 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedidos-${periodo}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      alert('Erro ao exportar os pedidos');
    } finally {
      setExportando(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  if (!stats) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">Não foi possível carregar as estatísticas.</p>
      </div>
    );
  }

  const formatarBRL = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
        <h1 className="text-3xl font-bold text-gray-100">📊 Dashboard</h1>
        <div className="flex items-center gap-2">
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value as PeriodoRelatorio)}
            className="input-field w-auto"
          >
            {Object.entries(PERIODOS_LABELS).map(([valor, label]) => (
              <option key={valor} value={valor}>
                {label}
              </option>
            ))}
          </select>
          <button onClick={exportarCsv} className="btn-secondary text-sm" disabled={exportando}>
            {exportando ? 'Gerando...' : '⬇️ Exportar CSV'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="card">
          <h3 className="text-sm font-medium text-gray-500">Faturamento</h3>
          <p className="text-3xl font-bold text-primary-400 mt-2">
            {formatarBRL(stats.periodo.receita)}
          </p>
          <p className="text-sm text-gray-400 mt-1">Comandas fechadas · {PERIODOS_LABELS[periodo]}</p>
        </div>

        <div className="card">
          <h3 className="text-sm font-medium text-gray-500">Pedidos</h3>
          <p className="text-3xl font-bold text-gray-100 mt-2">{stats.periodo.pedidos}</p>
          <p className="text-sm text-gray-400 mt-1">
            Ticket médio: {formatarBRL(stats.periodo.ticketMedio)}
          </p>
        </div>

        <Link to="/admin/orders" className="card hover:shadow-md transition-shadow">
          <h3 className="text-sm font-medium text-gray-500">Pendentes</h3>
          <p className="text-3xl font-bold text-orange-400 mt-2">{stats.pendentes}</p>
          <p className="text-sm text-gray-400 mt-1">Aguardando na cozinha</p>
        </Link>

        <div className="card">
          <h3 className="text-sm font-medium text-gray-500">Produtos Esgotados</h3>
          <p className={`text-3xl font-bold mt-2 ${stats.produtosEsgotados > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {stats.produtosEsgotados}
          </p>
          <p className="text-sm text-gray-400 mt-1">Indisponíveis no cardápio</p>
          {/* T127: insumo abaixo do mínimo não é "produto esgotado" — linha própria */}
          {stats.insumosAbaixoMinimo > 0 && (
            <p className="text-xs text-amber-400 mt-2">
              {stats.insumosAbaixoMinimo}{' '}
              {stats.insumosAbaixoMinimo === 1 ? 'insumo' : 'insumos'} abaixo do mínimo no estoque
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <h2 className="text-xl font-bold text-gray-100 mb-4">Mais Vendidos</h2>
          {stats.topProdutos.length === 0 ? (
            <p className="text-gray-400 text-sm">Nenhuma venda no período.</p>
          ) : (
            <div className="space-y-3">
              {stats.topProdutos.map((produto, i) => (
                <div key={produto.nome} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-gray-700 text-gray-300 text-xs flex items-center justify-center font-bold">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-200">{produto.nome}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-gray-100">
                      {produto.quantidade}x
                    </span>
                    <span className="text-xs text-gray-400 ml-2">
                      {formatarBRL(produto.receita)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="text-xl font-bold text-gray-100 mb-4">Mesas Mais Ativas</h2>
          {stats.topMesas.length === 0 ? (
            <p className="text-gray-400 text-sm">Nenhum pedido de mesa no período.</p>
          ) : (
            <div className="space-y-3">
              {stats.topMesas.map((mesa) => (
                <div key={mesa.mesa} className="flex items-center justify-between">
                  <span className="text-sm text-gray-200">Mesa {mesa.mesa}</span>
                  <span className="text-sm font-semibold text-gray-100">
                    {mesa.pedidos} pedido{mesa.pedidos > 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Acesso Rápido</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/admin/products" className="btn-primary text-center">
            Gerenciar Produtos
          </Link>
          <Link to="/admin/mesas" className="btn-primary text-center">
            Mesas e QR Codes
          </Link>
          <Link to="/admin/orders" className="btn-primary text-center">
            Ver Pedidos
          </Link>
        </div>
      </div>
    </div>
  );
}